import React, { useState } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { TotpChallengeRequiredError } from '../../services/cognitoAuthService';
import { FILLS, TYPE } from '../../styles/tokens';
import { User } from '../../types';
import { isValidEmail } from '../../utils/authFormValidation';
import { SheetFooter } from '../common/Sheet';

export interface LogInFormProps {
  /** Called with the signed-in user (null still counts as signed in). The form never navigates. */
  onSignedIn: (user: User | null) => void;
  submitLabel: string;
  idPrefix: string;
  onForgotPassword: () => void;
  prefilledEmail?: string;
  /** Minimum height of the forgot-password button, for a touch target. Unset keeps the text-sized button. */
  forgotPasswordMinHeight?: string;
  /** Called when the log-in is rejected as unconfirmed, so the host can open a code step. */
  onEmailUnverified: (credentials: { email: string; password: string }) => void;
  /** Called when the log-in is answered with an authenticator-code challenge, so the host can open the code step. */
  onTotpRequired: (challenge: { email: string; username: string; session: string }) => void;
  /**
   * Sign-in pop-up only: the submit button renders in the enclosing sheet's pinned footer
   * (outside the form, tied to it by `form=`). Unset keeps it inside the form, as the page does.
   */
  pinSubmitToSheetFooter?: boolean;
}

const INVALID_EMAIL_MESSAGE = 'Please enter a valid email address';

const isEmailUnverifiedError = (error: unknown): boolean => {
  const rawMessage = error instanceof Error ? error.message : '';
  return rawMessage === 'EMAIL_NOT_VERIFIED' || rawMessage.includes('not verified');
};

const toLogInErrorMessage = (error: unknown): string => {
  const rawMessage = error instanceof Error ? error.message : '';
  const errorCode =
    typeof error === 'object' && error !== null && 'code' in error ? error.code : undefined;
  if (rawMessage.includes('USER_PASSWORD_AUTH flow not enabled')) {
    return 'Authentication configuration error. Please contact support.';
  }
  if (rawMessage.includes('timeout')) {
    return 'Login is taking longer than expected. Please try again.';
  }
  if (rawMessage.includes('Network Error') || errorCode === 'NETWORK_ERROR') {
    return 'Network connection error. Please check your internet and try again.';
  }
  return rawMessage || 'Login failed. Please check your credentials and try again.';
};

const LogInForm: React.FC<LogInFormProps> = ({
  onSignedIn,
  submitLabel,
  idPrefix,
  onForgotPassword,
  prefilledEmail,
  forgotPasswordMinHeight,
  onEmailUnverified,
  onTotpRequired,
  pinSubmitToSheetFooter = false,
}) => {
  const [email, setEmail] = useState(prefilledEmail ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();

  const emailInputId = `${idPrefix}-email`;
  const passwordInputId = `${idPrefix}-password`;
  const errorMessageId = `${idPrefix}-error-message`;
  const formId = `${idPrefix}-form`;

  React.useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);

  const handleEmailBlur = () => {
    if (email.trim() === '') {
      setEmailError('');
      return;
    }
    setEmailError(isValidEmail(email.trim()) ? '' : INVALID_EMAIL_MESSAGE);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (emailError) {
      setEmailError('');
    }
    if (error) {
      setError('');
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    if (error) {
      setError('');
    }
  };

  const validateLoginFields = (): boolean => {
    setError('');
    setEmailError('');

    if (!email.trim()) {
      setError('Email address is required');
      return false;
    }
    if (!isValidEmail(email.trim())) {
      setEmailError(INVALID_EMAIL_MESSAGE);
      return false;
    }
    if (!password.trim()) {
      setError('Password is required');
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    if (!validateLoginFields()) {
      return;
    }

    setIsLoading(true);

    try {
      const userData = await login(email.trim(), password);
      onSignedIn(userData);
    } catch (caught: unknown) {
      setIsLoading(false);
      if (caught instanceof TotpChallengeRequiredError) {
        onTotpRequired({ email: email.trim(), ...caught.challenge });
        return;
      }
      if (isEmailUnverifiedError(caught)) {
        onEmailUnverified({ email: email.trim(), password });
        return;
      }
      setError(toLogInErrorMessage(caught));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await handleLogin();
  };

  const submitButton = (
    <button
      type="submit"
      form={formId}
      className="btn-gold"
      disabled={isLoading}
      style={pinSubmitToSheetFooter ? undefined : { marginTop: '24px' }}
    >
      {isLoading ? (
        <span className="spinner" style={{ width: '24px', height: '24px', margin: '0 auto' }} />
      ) : (
        submitLabel
      )}
    </button>
  );

  return (
    <>
      {(error || emailError) && (
        <div
          className="alert alert-error"
          id={errorMessageId}
          role="alert"
          style={{ marginBottom: '16px' }}
        >
          {error || emailError}
        </div>
      )}

      <form id={formId} onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor={emailInputId}>
            Email Address
          </label>
          <input
            id={emailInputId}
            className="form-input"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            required
            autoComplete="username"
            autoFocus={!prefilledEmail}
            aria-invalid={!!emailError || (!!error && error.includes('Email'))}
            aria-describedby={error ? errorMessageId : undefined}
            style={{
              ...(emailError || (error && error.includes('Email'))
                ? { borderColor: FILLS.error }
                : {}),
            }}
          />
          {emailError && <span className="form-error">{emailError}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor={passwordInputId}>
            Password
          </label>
          <input
            id={passwordInputId}
            className="form-input"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            required
            autoComplete="current-password"
            autoFocus={!!prefilledEmail}
            aria-invalid={!!error}
            aria-describedby={error ? errorMessageId : undefined}
            style={{
              ...(error && error.includes('Password') ? { borderColor: FILLS.error } : {}),
            }}
          />
          <div style={{ textAlign: 'right', marginTop: '8px' }}>
            <button
              type="button"
              onClick={onForgotPassword}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                ...TYPE.secondary,
                color: FILLS.textMuted,
                ...(forgotPasswordMinHeight
                  ? {
                      minHeight: forgotPasswordMinHeight,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }
                  : {}),
              }}
            >
              Forgot password?
            </button>
          </div>
        </div>

        {!pinSubmitToSheetFooter && submitButton}
      </form>
      {pinSubmitToSheetFooter && <SheetFooter>{submitButton}</SheetFooter>}
    </>
  );
};

export default LogInForm;
